import {isLatLngLiteral} from '@googlemaps/typescript-guards';
import {createCustomEqual} from 'fast-equals';
import {useEffect, useRef} from 'react';
import {TypedUseSelectorHook, useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from './store';
import {CurrencyEnum} from 'src/enums';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

const deepCompareEqualsForMaps = createCustomEqual((deepEqual: any): any => (a: any, b: any) => {
    if (
        isLatLngLiteral(a) ||
        a instanceof google.maps.LatLng ||
        isLatLngLiteral(b) ||
        b instanceof google.maps.LatLng
    ) {
        return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b);
});

export function useDeepCompareMemoize(value: any) {
    const ref = useRef();
    if (!deepCompareEqualsForMaps(value, ref.current)) {
        ref.current = value;
    }
    return ref.current;
}

export function useDeepCompareEffectForMaps(callback: React.EffectCallback, dependencies: any[]) {
    useEffect(callback, dependencies.map(useDeepCompareMemoize));
}

/**
 * Whether this caller sees only their own properties.
 *
 * Reads the scope the server derived from the role binding, not the role name. Keeping the hook's name
 * and signature means its twenty-two call sites need no edit while the thing underneath stops being a
 * hard-coded role - and it keeps working for a business-defined role whose scope is REAL_ESTATE.
 */
/**
 * The business the caller is working in.
 *
 * Their own, or - for a platform administrator, who has none - the one they picked in the sidebar.
 * A single answer for the whole app, because `authInfo.business.id` was read directly in fifty-odd
 * places and every one of them was a crash waiting for the first super admin to open that screen:
 * their `business` is null, and null has no id.
 */
export const useBusinessId = (): number => {
    const {authInfo, activeBusiness} = useAppSelector((state) => state.common);
    return authInfo?.business?.id ?? activeBusiness;
};

/**
 * The currency this business bills in.
 *
 * The single answer for everything that needs a currency before a price, tariff or cost item exists to
 * read one from: a new location's form, a cost item's default, and a statistics page whose figures
 * arrive with no currency attached when nothing matched. Each of those used to carry its own hardcoded
 * constant, and they did not even agree - three said CHF and one said EUR.
 */
export const useBusinessCurrency = (): string => {
    const {authInfo} = useAppSelector((state) => state.common);
    return authInfo?.business?.defaultCurrency ?? CurrencyEnum.CHF;
};

export const useDefineIsRealEstateRole = (): boolean => {
    const access = useAppSelector((state) => state.common.access);
    return access?.scope?.type === 'REAL_ESTATE';
};
